import {
  FundingSourceSelector,
  type ExternalAccount,
} from '@/src/common/components/fundingSourceSelector'
import { Button } from '@/src/common/components/Button'
import { useEffect, useState } from 'react'
import { ScrollView } from 'react-native'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { PlaidItemStatus } from '@api/mesa/graphql'
import type { PaymentMethods } from '@/src/experiences/card-payments/providePaymentMethods'
import { Plaid } from '@acre-rewards/lib'
import { useEvent } from 'tamagui'

const requiredProducts = [Plaid.Products.enum.Transfer] as const satisfies Parameters<
  typeof FundingSourceSelector
>[0]['requiredProducts']

export function DestinationFundingSourceSelector({
  storedAccount,
  setStoredAccount,
  pushToNextScreen,
  testID,
  accountListTitle,
  promptForUpdate = false,
  paymentMethods,
}: {
  storedAccount?: ExternalAccount | undefined
  setStoredAccount: (account: ExternalAccount) => void
  pushToNextScreen: () => void
  testID: string
  accountListTitle?: string
  promptForUpdate?: boolean
  paymentMethods: PaymentMethods
}) {
  const Alert = useAlert('Funding Source')

  const [selectedAccount, setSelectedAccount] = useState<ExternalAccount>()
  const onDataChange = useEvent(() => {
    setSelectedAccount(storedAccount ?? (promptForUpdate ? undefined : paymentMethods.data?.[0]))
  })

  useEffect(() => onDataChange(), [onDataChange])

  const onContinuePress = () => {
    if (selectedAccount?.isVerified) {
      setStoredAccount(selectedAccount)
      pushToNextScreen()
    } else {
      Alert.alert(
        'Destination Funding Account',
        `You can only use a verified account to transfer cash. Please verify your account and try again`,
        [{ text: 'OK', style: 'default' }],
      )
    }
  }

  const accountAvailable = paymentMethods.data ?? []

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
      <FundingSourceSelector
        title={accountListTitle}
        selectedAccount={selectedAccount}
        onAccountSelectedChange={(account) => {
          const updateRequired =
            promptForUpdate && account.status === PlaidItemStatus.UpdateRequired
          const productAvailable = requiredProducts.every((product) =>
            account.products.includes(product),
          )

          if (!productAvailable) {
            Alert.alert(
              'Destination Funding Account',
              `Please ensure your account is set up to allow for cash transfers.`,
              [
                {
                  text: 'Update',
                  style: 'default',
                  onPress: () => void paymentMethods.openPlaidLink?.(account),
                },
                {
                  text: 'Cancel',
                  style: 'cancel',
                },
              ],
            )
          } else if (updateRequired) {
            void paymentMethods.openPlaidLink?.(account)
          } else {
            setSelectedAccount(account)
          }
        }}
        loading={paymentMethods.loading || paymentMethods.plaidLinkLoading}
        accounts={accountAvailable}
        onAddNewAccountPress={() => void paymentMethods.openPlaidLink?.()}
        promptForUpdate={promptForUpdate}
        requiredProducts={requiredProducts}
      />
      <Button
        testID={testID}
        title="Continue"
        onPress={onContinuePress}
        disabled={!selectedAccount}
      />
    </ScrollView>
  )
}
